import { useState } from 'react';
import { localStorageBridge } from 'shared/src/utils/bridge';
import { useAsyncEffect } from 'shared/src/utils';

const CACHE_KEY = 'LIKE_GOODS_VOTE_BANNER';

const MIN_COUNT = 800;
const MAX_COUNT = 999;

const randomCount = () =>
  Math.floor(Math.random() * (MAX_COUNT - MIN_COUNT + 1)) + MIN_COUNT;

/** 직전 값과 겹치지 않는 새 카운트를 뽑는다. */
const pickCount = (prev: number | null) => {
  let next = randomCount();
  while (prev != null && next === prev) {
    next = randomCount();
  }
  return next;
};

/** 하루 단위 구분용 캐시 키 (사람이 읽는 날짜 포맷이 아님). */
const toDateKey = (d: Date) =>
  `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

type VoteCountCache = { count: number; dateKey: string };

const parseCache = (raw: string | null): VoteCountCache | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.count === 'number' &&
      typeof parsed?.dateKey === 'string'
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * 좋아요 탭 투표 배너의 노출 카운트.
 * native와 동일하게 800~999 범위에서 하루 동안 같은 값을 유지하고, 날짜가 바뀌면 갱신.
 */
export const useVoteBannerCount = () => {
  const [count, setCount] = useState<number | null>(null);

  useAsyncEffect(async () => {
    const cache = parseCache(await localStorageBridge.getString(CACHE_KEY));
    const todayKey = toDateKey(new Date());

    if (cache?.dateKey === todayKey) {
      setCount(cache.count);
      return;
    }

    const nextCount = pickCount(cache?.count ?? null);
    const nextCache: VoteCountCache = { count: nextCount, dateKey: todayKey };
    localStorageBridge.putString(CACHE_KEY, JSON.stringify(nextCache));
    setCount(nextCount);
  }, []);

  return count;
};
